/**
 * useDesignFeatureAccess — the access SEAM for the Solar Design tool (SD-2.1, FR-29; RBAC-6.2).
 *
 * Single source of truth for "may the current user use Design at all?". Built ON TOP of the
 * existing `PermissionContext` and the account registry (`useAccountFeatures`). Pure decision logic
 * lives in `@/lib/designAccess`; this hook only wires the live inputs to it.
 *
 * RBAC-6.2: the answer is the account's `solar_design` module plus `design.use`. The role list it
 * used to read from tenant_settings is gone.
 *
 * Contract (stable — consumed by the Design section inside the project Proposals tab and the sidebar):
 *   output — { hasAccess: boolean, loading: boolean }
 *
 * WHILE `loading` IS TRUE, CALLERS MUST RENDER NOTHING for Design, not a disabled or empty state.
 * SD-2.1 requires the section to be ABSENT FROM THE DOM when the gate is closed, and a surface
 * that renders first and hides on resolution has already told the user the feature exists. This is
 * a pre-release, metered feature; "briefly visible" is the failure this gate exists to prevent.
 *
 * This hook is a UI convenience and NOT a security boundary. Hiding a tab is not enforcement, so
 * every design endpoint is independently guarded server-side by `require_design_access` in
 * `app/services/solar/access.py`, which asks the database the same two questions.
 */
import { usePermissions } from '@/contexts/PermissionContext';
import { useAccountFeatures } from '@/hooks/useAccountFeatures';
import { DESIGN_USE_PERMISSION, computeDesignFeatureAccess } from '@/lib/designAccess';

export interface DesignFeatureAccess {
  hasAccess: boolean;
  loading: boolean;
}

export function useDesignFeatureAccess(): DesignFeatureAccess {
  const { hasPermission, loading: permsLoading } = usePermissions();
  // RBAC-6.1 / FR-19: the account switch in the feature registry comes before the permission.
  const { isOn, loading: featuresLoading } = useAccountFeatures();
  const loading = permsLoading || featuresLoading;

  const hasAccess = computeDesignFeatureAccess({
    accountEnabled: isOn('solar_design'),
    holdsDesignUse: hasPermission(DESIGN_USE_PERMISSION),
    isSuperadmin: hasPermission('system.override'),
  });
  return { hasAccess, loading };
}
